/**
 * Effective branding of a site: what the viewer actually gets after the
 * runtime's fallback chain — value saved under Theme & settings (runtime
 * `settings`), else the castr KV seed written at creation (`site.theme.*`),
 * else the runtime default (ott-cms `defaultSettings.colors`: selection
 * #0062FF, app background #141414, text #f7f8fa).
 */
export const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}){1,2}$/

export const RUNTIME_COLOR_DEFAULTS = Object.freeze({ primary: '#0062FF', background: '#141414', text: '#F7F8FA' })

/** runtime `colors.<key>` → castr `site.theme.<seed>` */
export const SEED_COLOR_KEYS = Object.freeze({ primary: 'primaryColor', background: 'backgroundColor', text: 'textColor' })

export const COLOR_FIELDS = [
	{ key: 'primary', label: 'Primary colour', hint: 'Buttons, links and highlights' },
	{ key: 'background', label: 'Background colour', hint: 'Page background' },
	{ key: 'text', label: 'Text colour', hint: 'Body text' },
]

const hex = (v) => {
	const s = String(v || '').trim()
	return HEX_COLOR.test(s) ? s : ''
}

/** The colour the site falls back to when nothing is saved under Theme & settings. */
export function seedColor(key, site) {
	return hex(site?.theme?.[SEED_COLOR_KEYS[key]]) || RUNTIME_COLOR_DEFAULTS[key]
}

/** First family of a CSS font list, unquoted — "\"Open Sans\", sans-serif" → "Open Sans". */
export function fontDisplayName(fontFamily) {
	const first = String(fontFamily || '').split(',')[0].trim().replace(/^["']|["']$/g, '')
	return first || ''
}

/**
 * @param {object} settings runtime `GET /settings → settings` (may be {} when unavailable)
 * @param {object} site castr site (for the KV seed)
 * @returns {{ logo: string, favicon: string, fontFamily: string, colors: { primary: string, background: string, text: string }, source: { logo: 'settings'|'seed'|'none', colors: Record<string,'settings'|'seed'|'default'>, fontFamily: 'settings'|'seed'|'default' } }}
 */
export function effectiveBranding(settings = {}, site = {}) {
	const s = settings || {}
	const seed = site?.theme || {}
	const colors = {}
	const colorSource = {}
	for (const { key } of COLOR_FIELDS) {
		const saved = hex(s.colors?.[key] || (key === 'text' ? s.colors?.textColor : ''))
		if (saved) { colors[key] = saved; colorSource[key] = 'settings'; continue }
		const seeded = hex(seed[SEED_COLOR_KEYS[key]])
		colors[key] = seeded || RUNTIME_COLOR_DEFAULTS[key]
		colorSource[key] = seeded ? 'seed' : 'default'
	}
	const logo = String(s.websiteLogo || '').trim() || String(seed.logoUrl || '').trim()
	const favicon = String(s.websiteFavicon || '').trim() || String(seed.faviconUrl || '').trim()
	const fontFamily = String(s.fontFamily || '').trim() || String(seed.fontFamily || '').trim()
	return {
		logo,
		favicon,
		fontFamily,
		colors,
		source: {
			logo: s.websiteLogo ? 'settings' : logo ? 'seed' : 'none',
			colors: colorSource,
			fontFamily: s.fontFamily ? 'settings' : fontFamily ? 'seed' : 'default',
		},
	}
}
